import { CSSProperties } from 'react'

import { ParkingConfig } from './types'

export type ParkingKind = 'free_fixed' | 'fixed' | 'unlimited' | 'unlock' | 'unlock_qr'

const PARKING_KIND_RANK: Record<ParkingKind, number> = {
  fixed: 1,
  free_fixed: 2,
  unlock: 3,
  unlock_qr: 4,
  unlimited: 5,
}

const getParkingKind = (
  config: Pick<ParkingConfig, 'parkingType' | 'isFree' | 'externalTicket'>,
): ParkingKind => {
  if (config.isFree) return 'free_fixed'
  if (config.externalTicket) return 'unlock_qr'
  return config.parkingType
}

const getParkingKindRank = (
  config: Pick<ParkingConfig, 'parkingType' | 'isFree' | 'externalTicket'>,
): number => PARKING_KIND_RANK[getParkingKind(config)]

const todayDateKey = (): string => {
  const today = new Date()
  return [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0'),
  ].join('-')
}

const toDateKey = (value?: string | null): string => (value ? String(value).slice(0, 10) : '')

const isParkingConfigEffective = (
  config: Pick<ParkingConfig, 'active' | 'isFree' | 'validFrom' | 'validTo'>,
): boolean => {
  if (!config.active) return false
  if (!config.isFree) return true

  const validFrom = toDateKey(config.validFrom)
  const validTo = toDateKey(config.validTo)
  if (!validFrom || !validTo) return false
  const todayKey = todayDateKey()
  return todayKey >= validFrom && todayKey <= validTo
}

/** After the inclusive validTo day, dates are read-only — create a new campaign instead. */
const isFreeFixedValidityLocked = (
  config?: Pick<ParkingConfig, 'isFree' | 'validTo'> | null,
): boolean => {
  if (!config?.isFree) return false
  const validTo = toDateKey(config.validTo)
  if (!validTo) return false
  return todayDateKey() > validTo
}

const getParkingKindI18nKey = (kind: ParkingKind): string => `parking:kinds.${kind}`

const DEFAULT_MEMBERSHIP_CHIP_STYLE: CSSProperties = {
  backgroundColor: '#e0e0e0',
  color: '#1A171B',
  border: 'none',
}

/** Solid level pills for parking list (PCEV-40 design update). */
const MEMBERSHIP_LEVEL_CHIP_STYLES: Record<number, CSSProperties> = {
  1: { backgroundColor: '#E0061A', color: '#ffffff', border: 'none' },
  2: { backgroundColor: '#e0e0e0', color: '#1A171B', border: 'none' },
  3: { backgroundColor: '#1a1a1a', color: '#ffffff', border: 'none' },
}

const getMembershipChipStyle = (level?: number): CSSProperties =>
  (level != null && MEMBERSHIP_LEVEL_CHIP_STYLES[level]) || DEFAULT_MEMBERSHIP_CHIP_STYLE

export {
  getMembershipChipStyle,
  getParkingKind,
  getParkingKindI18nKey,
  getParkingKindRank,
  isFreeFixedValidityLocked,
  isParkingConfigEffective,
}
